import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';
import Toast from 'react-native-root-toast';

import {
	AUSTRALIAN_STATES,
	CLIENT_LEAD_SOURCE,
	CLIENT_LEAD_STATE,
	CLIENT_LEAD_STATUS,
	CLIENT_LEAD_STATUS_NAMES,
	CLIENT_LEAD_TYPE,
} from '../../../common/constants';
import { CREATE_MEDIA } from '../../../common/graphql/media';
import { CREATE_NOTE } from '../../../common/graphql/note';
import { useUserContext } from '../../../common/utils/context';
import {
	CREATE_MOBILE_LEAD,
	GET_MOBILE_LEADS,
	MODIFY_MOBILE_LEAD,
} from '../../graphql/lead';
import { Picker, Text, TextInput } from '../../components';
import { VisualDirectionPhotoGrid } from '../../components/image';
import { Alert } from '../../components/other/Alert';
import withMediaPicker from '../../hocs/withMediaPicker';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { ActionDock, Card, EmptyState, Screen, SecondaryButton, SectionTitle } from '../../design/components';
import { useOfflineQueue } from '../../offline';
import LeadChoiceChips from './LeadChoiceChips';
import LeadContactFields, { EMPTY_LEAD_CONTACT, getLeadContactInput } from './LeadContactFields';
import LeadFollowUpDateInput, { FOLLOW_UP_DATE_FORMAT, getFollowUpDate } from './LeadFollowUpDateInput';
import LeadMatchList from './LeadMatchList';

const LAST_STATE_STORAGE_KEY = 'mobileLeadLastState';
const MATCH_MIN_LENGTH = 3;
const MATCH_DELAY_MS = 400;

const STATE_OPTIONS = [
	...Object.values(AUSTRALIAN_STATES),
	CLIENT_LEAD_STATE.NATIONAL_MULTIPLE_STATES,
].map(value => ({ label: value, value }));

const STATUS_OPTIONS = [
	CLIENT_LEAD_STATUS.CONTACTED,
	CLIENT_LEAD_STATUS.HOT_LEAD,
	CLIENT_LEAD_STATUS.WARM_LEAD,
	CLIENT_LEAD_STATUS.COLD_LEAD,
].map(value => ({ label: CLIENT_LEAD_STATUS_NAMES[value], value }));

const isRemoteUrl = url => typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));

const LeadCreatorView = ({
	captureMedia,
	capturedMediaBatch,
	processingMedia,
}) => {
	const styles = useThemedStyles(createStyles);
	const navigation = useNavigation();
	const { user, canManageClientsLeads } = useUserContext();
	const { isOnline } = useOfflineQueue();
	const [values, setValues] = useState(() => ({
		name: '',
		suburb: '',
		state: '',
		note: '',
		status: CLIENT_LEAD_STATUS.CONTACTED,
		followUpDate: getFollowUpDate(7),
	}));
	const [contact, setContact] = useState(EMPTY_LEAD_CONTACT);
	const [photoLinks, setPhotoLinks] = useState([]);
	const [showErrors, setShowErrors] = useState(false);
	const [matchQuery, setMatchQuery] = useState('');
	// Set once the lead exists, so retrying a failed note or photo never creates a second lead.
	const [createdLeadId, setCreatedLeadId] = useState(null);
	const [isSaving, setIsSaving] = useState(false);
	// Steps already done after the lead was created, so a retry only repeats what failed.
	const progressRef = useRef({ noteSaved: false, mediaIds: {}, photosLinked: false });
	const lastCapturedMediaBatchIdRef = useRef(null);

	const [createLead] = useMutation(CREATE_MOBILE_LEAD);
	const [createNote] = useMutation(CREATE_NOTE);
	const [createMedia] = useMutation(CREATE_MEDIA);
	const [modifyLead] = useMutation(MODIFY_MOBILE_LEAD);

	const setValue = useCallback(field => value => {
		setValues(current => ({ ...current, [field]: value }));
	}, []);

	const changeContact = useCallback((field, value) => {
		setContact(current => ({ ...current, [field]: value }));
	}, []);

	// Like Sentry Clients, start with the state used for the previous lead.
	useEffect(() => {
		AsyncStorage.getItem(LAST_STATE_STORAGE_KEY)
			.then(state => {
				if (state) {
					setValues(current => (current.state ? current : { ...current, state }));
				}
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		const name = values.name.trim();
		const timeout = setTimeout(() => setMatchQuery(name.length >= MATCH_MIN_LENGTH ? name : ''), MATCH_DELAY_MS);

		return () => clearTimeout(timeout);
	}, [values.name]);

	const { data: matchData } = useQuery(GET_MOBILE_LEADS, {
		variables: {
			page: 0,
			perPage: 3,
			sortBy: 'created',
			sortDirection: -1,
			filter: { query: matchQuery },
		},
		skip: !matchQuery || Boolean(createdLeadId) || !isOnline,
		fetchPolicy: 'cache-and-network',
	});
	const matches = matchQuery ? (matchData?.getLeads?.items || []) : [];

	useEffect(() => {
		if (!capturedMediaBatch || capturedMediaBatch.id === lastCapturedMediaBatchIdRef.current) {
			return;
		}

		lastCapturedMediaBatchIdRef.current = capturedMediaBatch.id;
		const links = capturedMediaBatch.links || [];
		// Offline captures come back as local files, which cannot be saved to the lead.
		const remoteLinks = links.filter(isRemoteUrl);
		setPhotoLinks(current => [...current, ...remoteLinks]);
		if (remoteLinks.length < links.length) {
			Toast.show(links.length - remoteLinks.length === 1
				? 'A photo could not be uploaded'
				: `${links.length - remoteLinks.length} photos could not be uploaded`);
		}
	}, [capturedMediaBatch]);

	const photos = useMemo(() => photoLinks.map(link => ({ id: link, link })), [photoLinks]);

	const missing = useMemo(() => ({
		name: !values.name.trim(),
		suburb: !values.suburb.trim(),
		state: !values.state,
		followUpDate: !values.followUpDate,
	}), [values.followUpDate, values.name, values.state, values.suburb]);

	const addPhotos = useCallback(() => {
		if (!isOnline) {
			Alert.alert('Internet connection required', 'Lead photos need to upload before they can be saved.');
			return;
		}

		if (!processingMedia && !isSaving) {
			captureMedia();
		}
	}, [captureMedia, isOnline, isSaving, processingMedia]);

	const removePhoto = useCallback((photo) => {
		setPhotoLinks(current => current.filter(link => link !== photo.link));
	}, []);

	const openLead = useCallback((leadId) => {
		navigation.replace('LeadDetailView', { id: leadId });
	}, [navigation]);

	const finishLead = useCallback(async (leadId, note) => {
		const progress = progressRef.current;

		try {
			if (note && !progress.noteSaved) {
				await createNote({ variables: { input: { lead: leadId, description: note } } });
				progress.noteSaved = true;
			}
		}
		catch (error) {
			Toast.show(error?.message || 'The note could not be saved');
			return false;
		}

		try {
			for (const link of photoLinks) {
				if (!progress.mediaIds[link]) {
					const result = await createMedia({ variables: { input: { lead: leadId, link } } });
					const mediaId = result?.data?.createMedia?.id;
					if (!mediaId) {
						throw new Error('The photos could not be saved');
					}
					progress.mediaIds[link] = mediaId;
				}
			}

			if (photoLinks.length && !progress.photosLinked) {
				await modifyLead({
					variables: {
						input: {
							id: leadId,
							photos: photoLinks.map(link => progress.mediaIds[link]),
						},
					},
				});
				progress.photosLinked = true;
			}
		}
		catch (error) {
			Toast.show(error?.message || 'The photos could not be saved');
			return false;
		}

		return true;
	}, [createMedia, createNote, modifyLead, photoLinks]);

	const submit = useCallback(async () => {
		if (isSaving || processingMedia) {
			return;
		}

		if (!isOnline) {
			Alert.alert('Internet connection required', 'New leads need internet to save. Try again when you are back online.');
			return;
		}

		const note = values.note.trim();

		if (createdLeadId) {
			setIsSaving(true);
			const isFinished = await finishLead(createdLeadId, note);
			setIsSaving(false);

			if (isFinished) {
				Toast.show('Lead added');
				openLead(createdLeadId);
			}
			return;
		}

		if (Object.values(missing).some(Boolean)) {
			setShowErrors(true);
			Toast.show('Fill in the required fields');
			return;
		}

		setIsSaving(true);
		let leadId = null;

		try {
			const result = await createLead({
				variables: {
					input: {
						name: values.name.trim(),
						suburb: values.suburb.trim(),
						state: values.state,
						type: CLIENT_LEAD_TYPE.SALES_LEAD,
						status: values.status,
						source: CLIENT_LEAD_SOURCE.COLD_CALL,
						assignee: user?.id,
						referrer: null,
						noReferrer: true,
						nextFollowUpAt: moment(values.followUpDate, FOLLOW_UP_DATE_FORMAT).startOf('day').toDate(),
						...getLeadContactInput(contact),
					},
				},
			});
			leadId = result?.data?.createLead?.id || null;
		}
		catch (error) {
			setIsSaving(false);
			Toast.show(error?.message || 'The lead could not be saved');
			return;
		}

		if (!leadId) {
			setIsSaving(false);
			Toast.show('The lead could not be saved');
			return;
		}

		AsyncStorage.setItem(LAST_STATE_STORAGE_KEY, values.state).catch(() => {});
		setCreatedLeadId(leadId);

		const isFinished = await finishLead(leadId, note);
		setIsSaving(false);

		if (isFinished) {
			Toast.show('Lead added');
			openLead(leadId);
		}
	}, [contact, createLead, createdLeadId, finishLead, isOnline, isSaving, missing, openLead, processingMedia, user?.id, values]);

	if (!canManageClientsLeads) {
		return (
			<Screen backLabel="Leads">
				<EmptyState icon="lock" title="No access" message="You do not have access to add leads." />
			</Screen>
		);
	}

	const renderRequired = field => (
		showErrors && missing[field] && <Text style={styles.required}>Required</Text>
	);

	let submitLabel = 'Save lead';
	if (createdLeadId) {
		submitLabel = 'Finish saving';
	}

	return (
		<Screen
			backLabel="Leads"
			heading="New cold call"
			subheading="Company, contact and what happened, in one go."
			dock={(
				<ActionDock
					action={{
						label: submitLabel,
						onPress: submit,
						loading: isSaving,
						disabled: isSaving || processingMedia,
					}}
				/>
			)}
		>
			<Card>
				<SectionTitle title="Company" style={styles.sectionTitle} />
				<View style={styles.field}>
					<Text style={styles.fieldLabel}>Company name</Text>
					<TextInput
						value={values.name}
						onChange={setValue('name')}
						disabled={Boolean(createdLeadId)}
						autoCapitalize="words"
					/>
					{renderRequired('name')}
					{matches.length > 0 && <LeadMatchList matches={matches} />}
				</View>
				<View style={styles.field}>
					<Text style={styles.fieldLabel}>Suburb</Text>
					<TextInput
						value={values.suburb}
						onChange={setValue('suburb')}
						disabled={Boolean(createdLeadId)}
						autoCapitalize="words"
					/>
					{renderRequired('suburb')}
				</View>
				<View style={styles.field}>
					<Text style={styles.fieldLabel}>State</Text>
					<Picker
						options={STATE_OPTIONS}
						selectedValue={values.state}
						onValueChange={state => setValue('state')(state || '')}
						emptyValueLabel="Select state"
						disabled={Boolean(createdLeadId)}
						width="100%"
					/>
					{renderRequired('state')}
				</View>
			</Card>

			<Card>
				<Text style={[styles.sectionTitleText, styles.sectionTitle]} accessibilityRole="header">
					Contact <Text style={styles.optional}>(optional)</Text>
				</Text>
				<LeadContactFields
					values={contact}
					onChange={changeContact}
					disabled={Boolean(createdLeadId)}
				/>
			</Card>

			<Card>
				<Text style={[styles.sectionTitleText, styles.sectionTitle]} accessibilityRole="header">
					Photos <Text style={styles.optional}>(optional)</Text>
				</Text>
				<VisualDirectionPhotoGrid
					canEdit={!createdLeadId && !isSaving}
					emptyText="Business cards, machines already on site or anything worth keeping."
					onRemovePhoto={removePhoto}
					photos={photos}
				/>
				{
					!createdLeadId && (
						<SecondaryButton
							label="Add photos"
							icon="camera"
							onPress={addPhotos}
							loading={processingMedia}
							disabled={processingMedia || isSaving}
							style={styles.addPhotosButton}
						/>
					)
				}
			</Card>

			<Card>
				<SectionTitle title="What happened" style={styles.sectionTitle} />
				<View style={styles.field}>
					<Text style={styles.fieldLabel}>Note <Text style={styles.optional}>(optional)</Text></Text>
					<TextInput
						value={values.note}
						onChange={setValue('note')}
						multiline={true}
						maxLength={3000}
						autoCapitalize="sentences"
						style={styles.noteInput}
					/>
				</View>
				<View style={styles.field}>
					<Text style={styles.fieldLabel}>Status</Text>
					<LeadChoiceChips
						options={STATUS_OPTIONS}
						selectedValue={values.status}
						onSelect={setValue('status')}
						disabled={Boolean(createdLeadId)}
					/>
				</View>
				<View style={styles.field}>
					<Text style={styles.fieldLabel}>Next follow-up</Text>
					<LeadFollowUpDateInput
						value={values.followUpDate}
						onChange={setValue('followUpDate')}
						disabled={Boolean(createdLeadId)}
					/>
					{renderRequired('followUpDate')}
				</View>
			</Card>

			{
				Boolean(createdLeadId) && (
					<Text style={styles.savedLead}>
						The lead is saved, but the note or photos did not save. Tap Finish saving to try again.
					</Text>
				)
			}
			<Text style={styles.summary}>
				Saved as a Cold call lead assigned to you, with no referrer. Change the source or referrer in Sentry Clients if needed.
			</Text>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	sectionTitle: {
		marginBottom: 14,
	},
	sectionTitleText: {
		...type.sectionTitle,
		color: colors.text,
	},
	optional: {
		...type.secondary,
		color: colors.textSecondary,
	},
	field: {
		marginBottom: space.md,
		gap: 6,
	},
	fieldLabel: {
		...type.fieldLabel,
		color: colors.text,
	},
	required: {
		...type.label,
		color: colors.danger,
	},
	addPhotosButton: {
		marginTop: space.sm,
	},
	noteInput: {
		minHeight: 108,
	},
	savedLead: {
		...type.bodySmallSemibold,
		color: colors.danger,
	},
	summary: {
		...type.secondary,
		color: colors.textSecondary,
	},
});

const LeadCreatorViewWithMediaPicker = withMediaPicker(LeadCreatorView);

const LeadCreatorViewWithPicker = props => (
	<LeadCreatorViewWithMediaPicker
		s3Path="images/leads/"
		allowMultipleSelection={true}
		allowMultipleCapture={true}
		collectCapturedMediaBatch={true}
		{...props}
	/>
);

export default LeadCreatorViewWithPicker;
